import React, { useState, useEffect } from 'react';
import { arrayOf, number, shape, func, bool, string, oneOf } from 'prop-types';
import { useRouter } from 'next/router';
import { BooleanParam, NumberParam, StringParam, useQueryParams } from 'next-query-params';

import { makeStyles } from '@material-ui/core/styles';
import BackArrowIcon from '@material-ui/icons/ArrowBack';
import Box from '@material-ui/core/Box';

import Alert from '../Alert';
import SquareButton from '../../../shared/Buttons/SquareButton';
import { track } from '../../../../modules/Analytics/helpers';

import filterUTMParams from '../../../../utils/filterUTMParams';

const useStyles = makeStyles(theme => ({
  backArrowIconPrimary: {
    color: theme.palette.neutral.darkest,
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const NavigationBar = ({
  currentPageIndex,
  flow,
  text,
  onClick,
  isDisabled,
  isLoading,
  isReady,
  alert,
  reservation,
  overrideParams,
  noContinue,
}) => {
  const router = useRouter();
  const classes = useStyles();
  const [isOnValidComplete, setIsOnValidComplete] = useState(false);
  const [params, setParams] = useQueryParams({
    skipAddons: BooleanParam,
    referrer: StringParam,
    flow: NumberParam,
  });

  const lastPage = flow[currentPageIndex - 1];
  const currentPage = flow[currentPageIndex];
  const nextPage = flow[currentPageIndex + 1];

  const currentPageName = currentPage?.replace('/', '');

  const handleButtonOnClick = async () => {
    await onClick();
    setIsOnValidComplete(true);
  };

  const handleArrowOnClick = () => {
    if (params.referrer === 'mcc') {
      router.back();
    } else if (currentPageIndex === 0) {
      track('Choose Service - Back', {});
      window.location.href = `${process.env.NEXT_PUBLIC_BOOKING_SITE_URL}${filterUTMParams(window.location.search)}`;
    } else if (currentPage?.includes('checkout') && params.skipAddons) {
      // Special case to skip addons on back if addons errored first time through, skips back two steps in flow
      setParams({
        skipAddons: false,
      });
      router.push({
        pathname: flow[currentPageIndex - 2],
        search: window.location.search,
      });
    } else {
      router.push({
        pathname: lastPage,
        search: window.location.search,
      });
    }
  };

  useEffect(() => {
    track('Viewed Checkout Step', {
      step: currentPageIndex + 1,
    });
    track('Booking - Page Viewed', {
      page: currentPageName,
      ...(typeof params?.flow !== 'undefined' ? { flow: params?.flow } : {}),
      ...(reservation && { Reservation: reservation }),
      ...(typeof window !== 'undefined'
        ? { fullUrlLength: window?.location?.href?.length, queryParamLength: window?.location?.search?.length }
        : {}),
    });
    if (currentPageIndex === 0) {
      track('Booking - Entered Funnel', {
        page: currentPageName,
      });
    }
  }, []);

  useEffect(() => {
    if (isOnValidComplete && !isLoading && isReady) {
      track('Completed Checkout Step', {
        step: currentPageIndex + 1,
      });
      track('Booking - Page Completed', {
        page: currentPageName,
      });
      if (nextPage.includes('dashboard')) {
        window.location.href = `${nextPage}?${overrideParams}`;
      } else {
        router.push({
          pathname: nextPage,
          search: window.location.search,
          state: {
            previousPage: currentPageIndex,
          },
        });
      }
    }
  }, [isOnValidComplete, isLoading, isReady]);

  return (
    <Box
      bgcolor="neutral.lightest"
      boxShadow="0px -5px 15px rgba(0, 0, 0, 0.1);"
      position="fixed"
      bottom="0"
      width="100%"
      left="0"
      right="0"
      zIndex="3"
      testid="navbar"
    >
      <Box
        height={{ xs: '88px', md: '100px' }}
        display="flex"
        alignItems="center"
        width={{ xs: '100%', md: 600 }}
        maxWidth={600}
        margin="0 auto"
        justifyContent="space-between"
        px={{ xs: 2, md: 0 }}
      >
        <BackArrowIcon
          color="primary"
          onClick={handleArrowOnClick}
          classes={{ colorPrimary: classes.backArrowIconPrimary }}
        />
        <Box display={{ xs: 'none', md: 'flex' }} alignItems="center" justifyContent="center" width={350}>
          {alert?.message && <Alert alert={alert} />}
        </Box>
        {!noContinue && (
          <SquareButton
            id="navbar-continue"
            type="submit"
            onClick={handleButtonOnClick}
            disabled={isDisabled}
            loading={isLoading}
          >
            {text}
          </SquareButton>
        )}
      </Box>
    </Box>
  );
};

NavigationBar.propTypes = {
  currentPageIndex: number.isRequired,
  flow: arrayOf(string.isRequired).isRequired,
  text: string,
  isReady: bool,
  onClick: func,
  isDisabled: bool,
  isLoading: bool,
  reservation: shape({}),
  alert: shape({
    type: oneOf(['error', 'secondary']),
    message: string,
  }),
  overrideParams: string,
  noContinue: bool,
};

NavigationBar.defaultProps = {
  text: 'Continue',
  isReady: true,
  onClick: () => {},
  isDisabled: false,
  isLoading: false,
  reservation: null,
  alert: {},
  overrideParams: '',
  noContinue: false,
};

export default NavigationBar;
